import React, { useState, useEffect } from 'react';
import { apiGet, apiPost } from '../api';
import { PERM, useCan } from '../permissions';

const banUserDevice = (payload) => apiPost('/api/admin/ban-device', payload);
const manageUserAccount = (payload) => apiPost('/api/admin/manage-user', payload);

const UserDetailModal = ({ userId, onClose }) => {
    // Disabling and banning a device stay open to every admin; wiping the
    // account is the same reach as the Delete button on All Users, so it
    // answers to the same permission the server checks.
    const can = useCan();
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [userExists, setUserExists] = useState(false);

    useEffect(() => {
        const fetchUserData = async () => {
            if (!userId) { setLoading(false); return; }
            setLoading(true);
            try {
                const u = await apiGet(`/api/users/${userId}`);
                if (u && (u.id || u._id)) { setUserData(u); setUserExists(true); }
                else setUserExists(false);
            } catch { setUserExists(false); }
            setLoading(false);
        };
        fetchUserData();
    }, [userId]);

    const performAction = async (action, confirmMessage, payload) => {
        if (!window.confirm(confirmMessage)) return;
        setActionLoading(true);
        try {
            const result = await manageUserAccount(payload);
            if (result.success) { alert(result.message); onClose(); }
            else throw new Error(result.message);
        } catch (error) { alert(`Failed to ${action} account: ${error.message}`); }
        finally { setActionLoading(false); }
    };

    const handleBanDevice = async () => {
        if (!window.confirm("Ban this user and their device? They will be logged out and unable to use the app.")) return;
        setActionLoading(true);
        try {
            const result = await banUserDevice({ userId });
            if (result.success) { alert(result.message || 'User and device banned!'); onClose(); }
            else throw new Error(result.message || "An unknown error occurred.");
        } catch (error) { alert(`Failed to ban device: ${error.message}`); }
        finally { setActionLoading(false); }
    };

    const handleToggleDisable = () => {
        const isDisabled = userData?.disable === true;
        performAction(isDisabled ? 'enable' : 'disable',
            isDisabled ? "Enable this user's account?" : "Disable this user's account? They will not be able to log in.",
            { userId, action: isDisabled ? 'enable' : 'disable' });
    };

    const handleDeleteAccount = () => {
        performAction('delete', "This action is permanent and cannot be undone. Delete this account?", { userId, action: 'delete' });
    };

    const handleBackdropClick = (e) => { if (e.target === e.currentTarget) onClose(); };

    const infoRow = (label, value, highlight = false) => (
        <div style={{ marginBottom: 12 }}>
            <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 2 }}>{label}</p>
            <p style={{ fontSize: 14, fontWeight: 600, color: highlight ? '#ff4d6d' : '#f1f5f9', wordBreak: 'break-all' }}>{value || 'Not provided'}</p>
        </div>
    );

    return (
        <div onClick={handleBackdropClick} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 }}>
            <div style={{ background: '#13132b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 16, width: '100%', maxWidth: 580, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 25px 60px rgba(0,0,0,0.5)' }}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                    <div>
                        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>User Details</h2>
                        <p style={{ margin: '4px 0 0', fontSize: 12, color: 'rgba(255,255,255,0.4)', fontFamily: 'monospace' }}>Account ID: {userId}</p>
                    </div>
                    <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.08)', border: 'none', color: 'rgba(255,255,255,0.6)', width: 32, height: 32, borderRadius: '50%', cursor: 'pointer', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                </div>

                {/* Body */}
                <div style={{ padding: 24 }}>
                    {loading ? (
                        <div className="dark-loading"><div className="spinner" /><p>Loading user data...</p></div>
                    ) : !userExists ? (
                        <div style={{ textAlign: 'center', padding: '30px 0' }}>
                            <i className="fas fa-user-times" style={{ fontSize: 40, color: 'rgba(255,255,255,0.2)', display: 'block', marginBottom: 12 }} />
                            <h3 style={{ fontWeight: 600, marginBottom: 6 }}>User Not Found</h3>
                            <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13 }}>The requested user may have already been deleted.</p>
                            <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                                <button disabled className="btn-action" style={{ opacity: 0.4, cursor: 'not-allowed' }}>Account Already Deleted</button>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
                                <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 16 }}>
                                    <h3 style={{ fontSize: 13, fontWeight: 700, color: 'rgba(255,255,255,0.6)', marginBottom: 14, textTransform: 'uppercase', letterSpacing: 1 }}>Basic Info</h3>
                                    {infoRow('ID', userData.ID, true)}
                                    {infoRow('Username', userData.username)}
                                    {infoRow('Email', userData.email)}
                                    <div>
                                        <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 4 }}>Account Status</p>
                                        <span className={`badge ${userData.disable === true ? 'badge-red' : 'badge-green'}`}>
                                            {userData.disable === true ? 'Disabled' : 'Active'}
                                        </span>
                                    </div>
                                </div>
                                <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 16 }}>
                                    <h3 style={{ fontSize: 13, fontWeight: 700, color: 'rgba(255,255,255,0.6)', marginBottom: 14, textTransform: 'uppercase', letterSpacing: 1 }}>Device Info</h3>
                                    <div>
                                        <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 4 }}>Fingerprint Hash</p>
                                        <p style={{ fontSize: 12, fontFamily: 'monospace', color: '#f1f5f9', wordBreak: 'break-all', lineHeight: 1.6 }}>{userData.fingerprintHash || 'Not Available'}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Actions */}
                            <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 20 }}>
                                <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 14 }}>Admin Actions</h3>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                                    <button onClick={handleToggleDisable} disabled={actionLoading} className="btn-action" style={{
                                        background: userData.disable === true ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                                        color: userData.disable === true ? '#4ade80' : '#f87171',
                                        border: `1px solid ${userData.disable === true ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
                                        opacity: actionLoading ? 0.5 : 1
                                    }}>
                                        {actionLoading ? <><i className="fas fa-spinner fa-spin" style={{ marginRight: 6 }} />Processing...</> : (userData.disable === true ? <><i className="fas fa-check-circle" style={{ marginRight: 6 }} />Enable Account</> : <><i className="fas fa-ban" style={{ marginRight: 6 }} />Disable Account</>)}
                                    </button>
                                    <button onClick={handleBanDevice} disabled={actionLoading || !userData.fingerprintHash} className="btn-action warn" style={{ opacity: (actionLoading || !userData.fingerprintHash) ? 0.5 : 1 }}>
                                        <i className="fas fa-mobile-alt" style={{ marginRight: 6 }} />Ban Device
                                    </button>
                                    {can(PERM.DELETE_ACCOUNTS) && (
                                        <button onClick={handleDeleteAccount} disabled={actionLoading} className="btn-action delete" style={{ opacity: actionLoading ? 0.5 : 1 }}>
                                            <i className="fas fa-trash" style={{ marginRight: 6 }} />Delete Account
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '14px 24px', borderTop: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.02)' }}>
                    <button onClick={onClose} className="cancelbtn">Close</button>
                </div>
            </div>
        </div>
    );
};

export default UserDetailModal;
